// « Mains vives » (D-163, D-164) : d'où viennent l'image et les mains. La caméra avant du téléphone (getUserMedia, en
// miroir) et MediaPipe dans un worker (mv-worker.ts), tout sur le téléphone ; ou, pour les tests seulement, le mode d'essai :
// l'image dessinée des maquettes et des mains simulées qui jouent toutes seules (jamais montré aux utilisateurs : il ne
// s'ouvre qu'avec la clé `arceau.mv.essai` posée par les scripts de test).
// D-165 : la position (jambes fléchies) par le modèle de pose, dans un second worker, quelques fois par seconde ; s'il ne se
// charge pas sur l'appareil, le jeu continue sans (un avertissement au calage).
import { CAM_H, CAM_W, HAND_M, dist, rng, type HandObs, type PoseFrame, type Pt, type Side } from '../domain/mains-vives';
import { POSES, type Pose } from './mv-art';
import { drawSimCamera } from './mv-stage';

type G = CanvasRenderingContext2D;
export interface Seen { hands: HandObs[]; count: number; at: number }
/** Ce que le jeu dit à la source (le mode d'essai s'en sert pour jouer tout seul) ; `dribble` : la main qui dribble (niveau 4). */
export interface Drive { phase: 'search' | 'placed' | 'count' | 'play' | 'pause' | 'end'; aim: Pt | null; aimAt: number; dribble?: Side | null }
export interface Source {
  readonly kind: 'camera' | 'essai';
  draw(g: G, w: number, h: number): void;
  seen(): Seen;
  /** La lumière de l'image (0 à 255), relue de temps en temps ; null : pas encore. */
  luma(): number | null;
  tick(now: number, d: Drive): void;
  stop(): void;
  /** D-165 : la dernière image du modèle de pose (et quand elle est arrivée) ; l'état du modèle sur l'appareil. */
  pose(): { frame: PoseFrame | null; at: number };
  poseStatus(): PoseStatus;
}
export type PoseStatus = 'loading' | 'ready' | 'failed';
/** La pose quelques fois par seconde (la posture change lentement) : une image toutes les 250 ms au plus. */
const POSE_EVERY_MS = 250;
export type CameraProblem = 'refus' | 'absente' | 'ancien' | 'occupee' | 'modele';
export class CameraError extends Error { constructor(readonly kind: CameraProblem, msg: string = kind) { super(msg); } }

/** Le mode d'essai des tests (jamais proposé à l'écran). */
export const essaiMode = (): boolean => { try { return localStorage.getItem('arceau.mv.essai') === '1'; } catch { return false; } };

/** Ce navigateur a-t-il tout ce qu'il faut (caméra, worker, images) ? */
export function cameraSupported(): boolean {
  return !!navigator.mediaDevices?.getUserMedia && typeof Worker !== 'undefined' && typeof createImageBitmap === 'function' && typeof OffscreenCanvas !== 'undefined';
}

/** Dessine la vidéo en couvrant le cadre, en miroir (on se voit comme dans une glace). */
function cover(g: G, v: HTMLVideoElement, w: number, h: number): boolean {
  if (v.readyState < 2 || !v.videoWidth) return false;
  const s = Math.max(w / v.videoWidth, h / v.videoHeight), dw = v.videoWidth * s, dh = v.videoHeight * s;
  g.save(); g.translate(w, 0); g.scale(-1, 1); g.drawImage(v, (w - dw) / 2, (h - dh) / 2, dw, dh); g.restore();
  return true;
}

// --- La caméra et MediaPipe ---------------------------------------------------------------------------------------------
class CameraSource implements Source {
  readonly kind = 'camera' as const;
  private det = document.createElement('canvas');
  private dg: G;
  private lum = document.createElement('canvas');
  private lg: G;
  private busy = false;
  private last = 0;
  private sentAt = 0;
  private frames = 0;
  private light: number | null = null;
  private latest: Seen = { hands: [], count: 0, at: 0 };
  private gen = 1;
  private poseState: PoseStatus = 'loading';
  private poseBusy = false;
  private poseLast = 0;
  private poseFrame: { frame: PoseFrame | null; at: number } = { frame: null, at: 0 };

  constructor(private video: HTMLVideoElement, private stream: MediaStream, private worker: Worker, private onFail: (p: CameraProblem) => void,
    private poseWorker: Worker | null = null) {
    this.det.width = 360; this.det.height = 480;
    this.dg = this.det.getContext('2d', { willReadFrequently: false })!;
    this.lum.width = 24; this.lum.height = 32;
    this.lg = this.lum.getContext('2d', { willReadFrequently: true })!;
    worker.onmessage = (e: MessageEvent<{ type: string; id: number; hands?: { lm: [number, number][]; score: number }[] }>) => {
      const d = e.data;
      if (d.id !== this.gen) return;
      if (d.type === 'result') {
        this.busy = false;
        const hands: HandObs[] = [];
        for (const h of d.hands ?? []) {
          if (h.score < 0.5 || h.lm.length < 21) continue;
          const at = (i: number): Pt => ({ x: h.lm[i]![0] * CAM_W, y: h.lm[i]![1] * CAM_H });
          const palm = [0, 5, 9, 13, 17].map(at);
          hands.push({ x: palm.reduce((s, p) => s + p.x, 0) / 5, y: palm.reduce((s, p) => s + p.y, 0) / 5, size: dist(at(0), at(9)), wrist: at(0) });
        }
        this.latest = { hands: hands.slice(0, 4), count: hands.length, at: performance.now() };
      } else if (d.type === 'error') { this.busy = false; this.onFail('modele'); }
    };
    worker.onerror = () => this.onFail('modele');
    // La pose : prête, en panne (le jeu continue sans), ou une image de plus.
    if (poseWorker) {
      const fail = () => { this.poseState = 'failed'; this.poseBusy = false; };
      const t = setTimeout(() => { if (this.poseState === 'loading') fail(); }, 60000);
      poseWorker.onmessage = (e: MessageEvent<{ type: string; id: number; pose?: { lm: [number, number, number][]; world: [number, number, number][] | null } | null }>) => {
        const d = e.data;
        if (d.type === 'ready') { clearTimeout(t); this.poseState = 'ready'; return; }
        if (d.type === 'error') { clearTimeout(t); fail(); return; }
        if (d.type === 'result' && d.id === this.gen) {
          this.poseBusy = false;
          const p = d.pose;
          this.poseFrame = { at: performance.now(), frame: p ? { lm: p.lm.map(([x, y, v]) => ({ x: x * CAM_W, y: y * CAM_H, v })), world: p.world ? p.world.map(([x, y, z]) => ({ x, y, z })) : null } : null };
        }
      };
      poseWorker.onerror = () => { clearTimeout(t); fail(); };
    } else this.poseState = 'failed';
  }
  draw(g: G, w: number, h: number): void { cover(g, this.video, w, h); }
  seen(): Seen { return this.latest; }
  pose(): { frame: PoseFrame | null; at: number } { return this.poseFrame; }
  poseStatus(): PoseStatus { return this.poseState; }
  luma(): number | null { return this.light; }
  tick(now: number): void {
    if (this.busy) { if (now - this.sentAt > 8000) { this.busy = false; this.onFail('modele'); } return; }
    if (now - this.last < 60 || !cover(this.dg, this.video, 360, 480)) return;
    this.last = now;
    if (this.frames++ % 15 === 0) {
      try {
        this.lg.drawImage(this.det, 0, 0, 24, 32);
        const px = this.lg.getImageData(0, 0, 24, 32).data;
        let s = 0; for (let i = 0; i < px.length; i += 4) s += 0.299 * px[i]! + 0.587 * px[i + 1]! + 0.114 * px[i + 2]!;
        this.light = s / (px.length / 4);
      } catch { this.light = null; }
    }
    this.busy = true; this.sentAt = now;
    createImageBitmap(this.det).then(bitmap => {
      this.worker.postMessage({ type: 'frame', id: this.gen, time: now, bitmap }, [bitmap]);
    }).catch(() => { this.busy = false; });
    // La pose, à part (son propre worker), une image toutes les 250 ms au plus : les mains gardent leur rythme.
    if (this.poseWorker && this.poseState === 'ready' && !this.poseBusy && now - this.poseLast >= POSE_EVERY_MS) {
      this.poseBusy = true; this.poseLast = now;
      createImageBitmap(this.det).then(bitmap => {
        this.poseWorker?.postMessage({ type: 'frame', id: this.gen, time: now, bitmap }, [bitmap]);
      }).catch(() => { this.poseBusy = false; });
    } else if (this.poseBusy && now - this.poseLast > 4000) this.poseBusy = false;   // une image perdue : on reprend
  }
  stop(): void {
    this.gen++;
    try { this.worker.terminate(); } catch { /* déjà arrêté */ }
    try { this.poseWorker?.terminate(); } catch { /* déjà arrêté */ }
    this.stream.getTracks().forEach(t => t.stop());
    try { this.video.pause(); this.video.srcObject = null; this.video.remove(); } catch { /* déjà retirée */ }
  }
}

/** Ouvre la caméra avant (la demande d'autorisation du téléphone), puis charge le repérage des mains. */
export async function openCamera(host: HTMLElement, onFail: (p: CameraProblem) => void, onStep: (s: 'camera' | 'modele') => void): Promise<Source> {
  if (!cameraSupported()) throw new CameraError('ancien');
  onStep('camera');
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 960 }, frameRate: { ideal: 30 } }, audio: false });
  } catch (e) {
    const n = (e as { name?: string }).name ?? '';
    throw new CameraError(n === 'NotAllowedError' || n === 'SecurityError' ? 'refus' : n === 'NotReadableError' || n === 'AbortError' ? 'occupee' : 'absente', n);
  }
  const video = document.createElement('video');
  video.className = 'mv-video-src';
  video.muted = true; video.playsInline = true; video.autoplay = true;
  video.setAttribute('playsinline', ''); video.setAttribute('muted', ''); video.setAttribute('aria-hidden', 'true');
  video.srcObject = stream;
  host.append(video);
  try { await video.play(); } catch { /* l'image arrive quand même */ }
  onStep('modele');
  const worker = new Worker(new URL('./mv-worker.ts', import.meta.url), { type: 'module' });
  const base = new URL('/mediapipe/', location.href).href;
  try {
    await new Promise<void>((resolve, reject) => {
      const t = setTimeout(() => reject(new CameraError('modele', 'trop long')), 90000);
      worker.onmessage = (e: MessageEvent<{ type: string; message?: string }>) => {
        if (e.data.type === 'ready') { clearTimeout(t); resolve(); }
        if (e.data.type === 'error') { clearTimeout(t); reject(new CameraError('modele', e.data.message)); }
      };
      worker.onerror = () => { clearTimeout(t); reject(new CameraError('modele')); };
      worker.postMessage({ type: 'init', id: 1, base });
    });
  } catch (e) {
    worker.terminate(); stream.getTracks().forEach(t => t.stop()); video.remove();
    throw e;
  }
  // D-165 : la pose se charge à côté, sans faire attendre le jeu (la première fois, 6 Mo de plus).
  let poseWorker: Worker | null = null;
  try {
    poseWorker = new Worker(new URL('./mv-worker.ts', import.meta.url), { type: 'module' });
    poseWorker.postMessage({ type: 'init', id: 1, base, task: 'pose' });
  } catch { poseWorker = null; }
  return new CameraSource(video, stream, worker, onFail, poseWorker);
}

// --- Le mode d'essai (tests, captures) ----------------------------------------------------------------------------------------
type P2 = [number, number];
const READY: [P2, P2] = POSES.ready.ha;
const UP: [P2, P2] = POSES.up.ha;
/** La silhouette suit les mains simulées : bras pliés (deux segments), on descend sur les jambes pour une cible basse. */
function poseFor(L: Pt, R: Pt): Pose {
  const b = POSES.ready;
  const low = Math.max(0, Math.max(L.y, R.y) - 300);
  const dy = Math.min(60, low * 0.75), dx = (L.y > 300 ? -1 : R.y > 300 ? 1 : 0) * dy * 0.35;
  const mv = (p: P2, k = 1): P2 => [p[0] + dx * k, p[1] + dy * k];
  const sh: [P2, P2] = [mv(b.sh[0]), mv(b.sh[1])];
  const arm = (s: P2, h: Pt, out: number): P2 => {
    const L1 = 50, L2 = 50;
    let vx = h.x - s[0], vy = h.y - s[1];
    let d = Math.hypot(vx, vy) || 1;
    if (d > L1 + L2 - 1) { const k = (L1 + L2 - 1) / d; vx *= k; vy *= k; d = L1 + L2 - 1; }
    const m: P2 = [s[0] + vx / 2, s[1] + vy / 2];
    const off = Math.sqrt(Math.max(0, L1 * L1 - (d / 2) ** 2));
    let px = -vy / d, py = vx / d;
    if (px * out < 0) { px = -px; py = -py; }
    return [m[0] + px * off, m[1] + py * off];
  };
  return {
    head: mv(b.head), sh, hip: [mv(b.hip[0]), mv(b.hip[1])],
    kn: [[b.kn[0][0] - dy * 0.5, b.kn[0][1] + dy * 0.2], [b.kn[1][0] + dy * 0.5, b.kn[1][1] + dy * 0.2]], an: b.an,
    el: [arm(sh[0], L, -1), arm(sh[1], R, 1)], ha: [[L.x, L.y], [R.x, R.y]],
  };
}

class EssaiSource implements Source {
  readonly kind = 'essai' as const;
  private L: Pt = { x: READY[0][0], y: READY[0][1] };
  private R: Pt = { x: READY[1][0], y: READY[1][1] };
  private phase: Drive['phase'] | null = null;
  private since = 0;
  private prev = 0;
  private aimAt = -1;
  private delay = 600;
  private rand = rng(11);
  private visible = false;
  private ball: Pt | null = null;
  private standing = false;
  private poseAt = 0;
  draw(g: G): void { drawSimCamera(g, poseFor(this.L, this.R), this.ball); }
  poseStatus(): PoseStatus { return 'ready'; }
  /** La pose de la silhouette dessinée : en position (genoux à 140°) ; avec la clé `arceau.mv.essai.debout`, debout (178°)
   *  pendant le jeu, de la 4e à la 12e seconde (pour voir « Fléchis les jambes »). */
  pose(): { frame: PoseFrame | null; at: number } {
    if (!this.visible) return { frame: null, at: this.poseAt };
    const p = poseFor(this.L, this.R);
    const lm = Array.from({ length: 33 }, () => ({ x: 195, y: 200, v: 0.1 }));
    const put = (i: number, q: [number, number]) => { lm[i] = { x: q[0], y: q[1], v: 0.95 }; };
    put(11, p.sh[0]); put(12, p.sh[1]); put(23, p.hip[0]); put(24, p.hip[1]); put(25, p.kn[0]); put(26, p.kn[1]); put(27, p.an[0]); put(28, p.an[1]);
    const th = ((this.standing ? 178 : 140) * Math.PI) / 180;
    const world = Array.from({ length: 33 }, () => ({ x: 0, y: 0, z: 0 }));
    for (const [h, k, a, x] of [[23, 25, 27, -0.1], [24, 26, 28, 0.1]] as const) {
      world[k] = { x, y: 0, z: 0 }; world[h] = { x, y: 0.42, z: 0 }; world[a] = { x, y: 0.42 * Math.cos(th), z: 0.42 * Math.sin(th) };
    }
    return { frame: { lm, world }, at: this.poseAt };
  }
  seen(): Seen {
    if (!this.visible) return { hands: [], count: 0, at: performance.now() };
    const size = 155 * HAND_M;
    return { hands: [{ ...this.L, size }, { ...this.R, size }], count: 2, at: performance.now() };
  }
  luma(): number { return 120; }
  tick(now: number, d: Drive): void {
    const dt = this.prev ? Math.min(100, now - this.prev) : 16;
    this.prev = now;
    if (d.phase !== this.phase) { this.phase = d.phase; this.since = now; }
    const t = now - this.since;
    if (now - this.poseAt >= 250) this.poseAt = now;
    this.standing = d.phase === 'play' && t > 4000 && t < 12000 && (() => { try { return localStorage.getItem('arceau.mv.essai.debout') === '1'; } catch { return false; } })();
    let gl: Pt = { x: READY[0][0], y: READY[0][1] }, gr: Pt = { x: READY[1][0], y: READY[1][1] };
    if (d.phase === 'search') this.visible = t > 700;
    if (d.phase === 'placed' && t > 1400) { gl = { x: UP[0][0], y: UP[0][1] }; gr = { x: UP[1][0], y: UP[1][1] }; }
    if (d.phase === 'play' && d.aim) {
      // Une cible sur huit, la main simulée ne part pas (un raté, comme en vrai).
      if (d.aimAt !== this.aimAt) { this.aimAt = d.aimAt; this.delay = this.rand() < 0.125 ? 1e9 : 380 + this.rand() * 420; }
      if (now - d.aimAt >= this.delay) { if (d.aim.x < 195) gl = d.aim; else gr = d.aim; }
    }
    const k = Math.min(1, dt / 70);
    this.L = { x: this.L.x + (gl.x - this.L.x) * k, y: this.L.y + (gl.y - this.L.y) * k };
    this.R = { x: this.R.x + (gr.x - this.R.x) * k, y: this.R.y + (gr.y - this.R.y) * k };
    // Niveau 4 : la main qui dribble monte et descend à la hanche (2 rebonds par seconde, 25 cm), le ballon rebondit dessous.
    this.ball = null;
    if (d.dribble && d.phase !== 'pause') {
      const w = Math.cos((2 * Math.PI * 2 * now) / 1000);
      const hand: Pt = { x: d.dribble === 'R' ? READY[1][0] + 30 : READY[0][0] - 30, y: 290 - 19 * w };
      if (d.dribble === 'R') this.R = hand; else this.L = hand;
      this.ball = { x: hand.x + (d.dribble === 'R' ? 6 : -6), y: hand.y + 22 + (452 - hand.y - 22) * (1 + w) / 2 };
    }
  }
  stop(): void { /* rien à libérer */ }
}
export const essaiSource = (): Source => new EssaiSource();
